import flatten from "lodash/flatten";
import uniq from "lodash/uniq";
import { WSInfiniteQueryConfig } from "@ws-react-query";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { ApiQueryListWorkLogs, listWorkLogs } from "../../../services/workLog";
import { getPayeeRows, IPayeeRow } from "../../../services/search";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useWSInfiniteQuery } from "../../helpers";
import { useXWingspanExpansion } from "../../../shared/hooks/useXWingspanExpansion";

/** Page size for fetching work logs. Aligned with invoice pagination config pattern. */
const PAGE_SIZE = 20;

/** Page size for fetching payees. Aligned with invoice pagination config pattern. */
const PAYEE_BATCH_SIZE = 200;

export const QUERY_WORK_LOGS_WITH_PAYEES = "QUERY_WORK_LOGS_WITH_PAYEES";

type WorkLogWithPayee = IWorkLog & { payee?: IPayeeRow };

export const useQueryWorkLogsWithPayees = (
  requestQuery?: ApiQueryListWorkLogs,
  config?: {
    joinPayee?: boolean;
  } & WSInfiniteQueryConfig<WorkLogWithPayee[], WSServiceError>
) => {
  const xWingspanExpansion = useXWingspanExpansion();
  const joinPayee = config?.joinPayee || false;

  const query = useWSInfiniteQuery<WorkLogWithPayee[], WSServiceError>(
    [QUERY_WORK_LOGS_WITH_PAYEES, requestQuery, joinPayee, xWingspanExpansion],
    async ({ pageParam = 1 }) => {
      const workLogs = await listWorkLogs({
        ...requestQuery,
        page: {
          size: requestQuery?.page?.size || PAGE_SIZE,
          number: pageParam
        }
      });

      // Fetch payees if joinPayee is enabled
      if (!joinPayee) {
        return workLogs;
      }

      // Extract unique payee IDs from work logs
      const payeeIds = uniq(workLogs.map(workLog => workLog.payeeId));

      // Fetch payees in batch
      const payees =
        payeeIds.length > 0
          ? await getPayeeRows(
              {
                filter: {
                  payeeId: {
                    in: payeeIds
                  }
                },
                page: {
                  size: PAYEE_BATCH_SIZE
                }
              },
              xWingspanExpansion
            )
          : [];

      // Join payees to work logs
      return workLogs.map(workLog => ({
        ...workLog,
        payee: payees.find(payee => payee.payeeId === workLog.payeeId)
      }));
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = requestQuery?.page?.size || PAGE_SIZE;
        if (lastPage.length < pageSize) {
          return undefined;
        } else {
          return allPages.length + 1;
        }
      },
      ...config
    }
  );

  return {
    ...query,
    data: query.data ? flatten(query.data.pages) : undefined
  };
};
